import { useI18n } from "@wojo/localization";
import {
    AlertBasic,
    Button,
    Collapsible,
    Form,
    FormMaskedNumericField,
    FormRadioGroup,
    FormTextarea,
    FormTextField,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from "@wojo/ui";
import type { E164Number } from "libphonenumber-js";
import { useForm } from "react-hook-form";
import { Text } from "@wojo/ui";
import { LuPlusCircle, LuMinusCircle } from "react-icons/lu";
import styles from "./AddInterestedTravelerForm.module.scss";
import {
    GcDepartureProspectLevelOfInterest,
    useBulkCreateGroupMembersProspectsMutation,
    Address,
    BulkCreateGcDepartureProspectsInput,
    GroupMembersProspectsDocument,
} from "generated/graphql";
import { useState } from "react";
import { alpha2ToAlpha3 } from "i18n-iso-countries";

type AddInterestedTravelerFormValues = {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: E164Number;
    address: Address;
    levelOfInterest?: GcDepartureProspectLevelOfInterest;
    notes?: string;
};

export type AddInterestedTravelerFormProps = {
    isAddInterestedTravelerModalOpen: boolean;
    onCloseAddInterestedTravelerModal: () => void;
    groupCoordinatorCustomerId: string;
    gcDepartureId: string;
};

export const AddInterestedTravelerForm: React.FC<
    AddInterestedTravelerFormProps
> = ({
    isAddInterestedTravelerModalOpen,
    onCloseAddInterestedTravelerModal,
    groupCoordinatorCustomerId,
    gcDepartureId,
}) => {
    const [createBulkGroupMembersProspects, { loading }] =
        useBulkCreateGroupMembersProspectsMutation({
            refetchQueries: [
                {
                    query: GroupMembersProspectsDocument,
                    variables: {
                        gcDepartureId,
                    },
                },
            ],
        });
    const [error, setError] = useState<string | null>(null);
    const [isMailingAddressExpanded, setIsMailingAddressExpanded] =
        useState(false);

    const { global } = useI18n("global");
    const globalFormLoc = global.formFieldLabels;
    const addInterestedTravelerFormLoc =
        useI18n("account").account.myGroupMembers
            .addInterestedTravelerFormDetails;

    const isCanada = process.env.NEXT_PUBLIC_MARKET_CODE === "CA";

    const levelOfInterestOptions = [
        {
            value: GcDepartureProspectLevelOfInterest.High,
            label: addInterestedTravelerFormLoc.levelOfInterestOptions.high.l(),
        },
        {
            value: GcDepartureProspectLevelOfInterest.Medium,
            label: addInterestedTravelerFormLoc.levelOfInterestOptions.medium.l(),
        },
        {
            value: GcDepartureProspectLevelOfInterest.Low,
            label: addInterestedTravelerFormLoc.levelOfInterestOptions.low.l(),
        },
    ];

    const form = useForm<AddInterestedTravelerFormValues>({
        defaultValues: {
            firstName: "",
            lastName: "",
            email: "",
            phoneNumber: "",
            address: {
                street1: "",
                street2: "",
                city: "",
                state: "",
                postalCode: "",
                country: "",
            },
            levelOfInterest: undefined,
            notes: "",
        },
        mode: "onSubmit",
        reValidateMode: "onChange",
        criteriaMode: "firstError",
    });

    const handleSubmit = form.handleSubmit(async (values) => {
        const input: BulkCreateGcDepartureProspectsInput = {
            gcDepartureId,
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            phoneNumber: values.phoneNumber,
            address: {
                ...values.address,
                country:
                    alpha2ToAlpha3(process.env.NEXT_PUBLIC_MARKET_CODE || "") ||
                    "USA",
            },
            levelOfInterest: values.levelOfInterest,
            notes: values.notes,
        };

        const result = await createBulkGroupMembersProspects({
            variables: {
                groupCoordinatorCustomerId,
                input,
            },
            awaitRefetchQueries: true,
            onError: () => {
                setError(addInterestedTravelerFormLoc.genericError.l());
            },
        });

        if (
            result.data?.bulkCreateGCDepartureProspects.__typename ===
            "BulkCreateGCDepartureProspectsSuccess"
        ) {
            onCloseAddInterestedTravelerModal();
            setIsMailingAddressExpanded(false);
            form.reset();
        }
    });

    return (
        <Modal
            isOpen={isAddInterestedTravelerModalOpen}
            onCloseRequested={() => {
                onCloseAddInterestedTravelerModal();
                setIsMailingAddressExpanded(false);
                form.reset();
            }}
            accessibleTitleId={addInterestedTravelerFormLoc.title.l()}
        >
            <ModalHeader
                displayClose={false}
                className={styles["modal-header"]}
            >
                <Text.Heading order="3" tag="h1">
                    {addInterestedTravelerFormLoc.title.l()}
                </Text.Heading>
                <Text.Body>
                    {addInterestedTravelerFormLoc.subtitle.l()}
                </Text.Body>
            </ModalHeader>
            <ModalBody>
                {error && (
                    <AlertBasic
                        id="graphql-error"
                        status="alert"
                        title={error}
                        style={{
                            marginBottom: "var(--g-spacing-md)",
                        }}
                    />
                )}
                <Form form={form} className={styles.form}>
                    <FormTextField
                        name={"firstName"}
                        label={globalFormLoc.firstName.l()}
                        validation={{
                            required:
                                addInterestedTravelerFormLoc.firstNameError.l(),
                        }}
                    />
                    <FormTextField
                        name={"lastName"}
                        label={globalFormLoc.lastName.l()}
                        validation={{
                            required:
                                addInterestedTravelerFormLoc.lastNameError.l(),
                        }}
                    />
                    <FormTextField
                        name={"email"}
                        label={globalFormLoc.email.l()}
                        inputMode="email"
                        validation={{
                            required:
                                addInterestedTravelerFormLoc.emailError.l(),
                            validate: (value) => {
                                const regex = /^[^\s@]+@[^\s@]+$/;
                                return (
                                    !value ||
                                    regex.test(value) ||
                                    addInterestedTravelerFormLoc.emailError.l()
                                );
                            },
                        }}
                    />
                    <FormMaskedNumericField
                        name={"phoneNumber"}
                        label={globalFormLoc.phone.l()}
                        showOptionalValidationText={true}
                        format={global.inputMasks.phone.l()}
                        inputMode="numeric"
                    />
                    <div className={styles["form-lower-half"]}>
                        <Collapsible
                            title={
                                <Button
                                    tag="span"
                                    variant="quiet"
                                    adornLeft={
                                        isMailingAddressExpanded ? (
                                            <LuMinusCircle />
                                        ) : (
                                            <LuPlusCircle />
                                        )
                                    }
                                >
                                    {addInterestedTravelerFormLoc.mailingAddressTitle.l()}
                                </Button>
                            }
                            headerClassName={
                                styles["mailing-address-collapsible-trigger"]
                            }
                            expanded={isMailingAddressExpanded}
                            hideBottomBorder={true}
                            hidePadding={true}
                            hideTopBorder={true}
                            hideIcon={true}
                            onToggle={(isExpanded) =>
                                setIsMailingAddressExpanded(isExpanded)
                            }
                        >
                            <div className={styles["mailing-address"]}>
                                <FormTextField
                                    name={"address.street1"}
                                    label={globalFormLoc.addressFields.addressLine1.l()}
                                    showOptionalValidationText={true}
                                />
                                <FormTextField
                                    name={"address.street2"}
                                    label={globalFormLoc.addressFields.addressLine2.l()}
                                    showOptionalValidationText={true}
                                />
                                <FormTextField
                                    name={"address.city"}
                                    label={globalFormLoc.addressFields.city.l()}
                                    showOptionalValidationText={true}
                                />
                                <FormTextField
                                    name={"address.state"}
                                    label={
                                        isCanada
                                            ? globalFormLoc.addressFields.province.l()
                                            : globalFormLoc.addressFields.state.l()
                                    }
                                    showOptionalValidationText={true}
                                />
                                <FormTextField
                                    name={"address.postalCode"}
                                    label={
                                        isCanada
                                            ? globalFormLoc.addressFields.postalCode.l()
                                            : globalFormLoc.addressFields.zipCode.l()
                                    }
                                    showOptionalValidationText={true}
                                />
                            </div>
                        </Collapsible>
                        <FormRadioGroup
                            name={"levelOfInterest"}
                            title={addInterestedTravelerFormLoc.levelOfInterestTitle.l()}
                            items={levelOfInterestOptions}
                            showOptionalValidationText={true}
                        />
                        <FormTextarea
                            name={"notes"}
                            label={addInterestedTravelerFormLoc.notes.l()}
                            showOptionalValidationText={true}
                            maxLength={200}
                            showMaxLengthText={true}
                        />
                    </div>
                </Form>
            </ModalBody>
            <ModalFooter className={styles["modal-footer"]}>
                <Button
                    loading={loading}
                    onClick={async () => {
                        await handleSubmit();
                    }}
                >
                    {addInterestedTravelerFormLoc.addTravelerSubmitText.l()}
                </Button>
            </ModalFooter>
        </Modal>
    );
};
